import React, { useState, useEffect } from 'react';
import { Save, Mail, ToggleLeft, ToggleRight, Eye, EyeOff, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getEmailSettings, saveEmailSettings, sendBookingEmail } from '../lib/api';

const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
    {hint && <p className="text-xs text-gray-400 mb-1.5">{hint}</p>}
    {children}
  </div>
);

const EmailSettingsPage = () => {
  const [form, setForm] = useState({
    brevo_api_key: '',
    sender_email: '',
    sender_name: 'VietClinics',
    clinic_notification_email: '',
    enabled: false,
  });
  const [hasStoredKey, setHasStoredKey] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    getEmailSettings()
      .then((res) => {
        const d = res.data?.data || {};
        setForm((p) => ({
          ...p,
          sender_email: d.sender_email || '',
          sender_name: d.sender_name || 'VietClinics',
          clinic_notification_email: d.clinic_notification_email || '',
          enabled: d.enabled || false,
          brevo_api_key: d.has_api_key ? '••••••••' + (d.brevo_api_key || '').slice(-4) : '',
        }));
        setHasStoredKey(!!d.has_api_key);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!form.sender_email) { toast.error('Sender email is required'); return; }
    setSaving(true);
    try {
      await saveEmailSettings(form);
      toast.success('Settings saved successfully');
      setHasStoredKey(!!form.brevo_api_key && !form.brevo_api_key.startsWith('••••'));
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) { toast.error('Enter a test email address'); return; }
    if (!form.enabled) { toast.error('Enable email notifications first'); return; }
    setTesting(true);
    try {
      const res = await sendBookingEmail({
        patientName: 'VietClinics Test',
        patientEmail: testEmail,
        clinicName: 'Test Clinic',
        preferredDate: new Date().toISOString().split('T')[0],
        preferredTime: '10:00',
        bookingRef: 'VC-TEST-001',
        source: 'test',
      });
      const status = res.data?.status;
      if (status === 'sent') toast.success('Test email sent! Check your inbox.');
      else if (status === 'skipped') toast.warning(`Skipped: ${res.data?.message}`);
      else toast.error('Unexpected response');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test failed — check your API key');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1FA7A1] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-12 max-w-2xl">
        {/* Page title */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 bg-[#1FA7A1]/10 rounded-xl flex items-center justify-center">
            <Mail className="h-5 w-5 text-[#1FA7A1]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Email Settings</h1>
            <p className="text-sm text-gray-500">Configure Brevo (Sendinblue) for booking notifications</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 space-y-6">

          {/* Enable toggle */}
          <div className="flex items-center justify-between pb-5 border-b border-gray-100">
            <div>
              <p className="font-semibold text-gray-900">Email Notifications</p>
              <p className="text-sm text-gray-500">Send booking emails to patients and clinic</p>
            </div>
            <button
              onClick={() => setForm((p) => ({ ...p, enabled: !p.enabled }))}
              className="flex items-center gap-2 text-sm font-medium transition-colors"
              data-testid="email-toggle"
            >
              {form.enabled ? (
                <><ToggleRight className="h-8 w-8 text-[#1FA7A1]" /><span className="text-[#1FA7A1]">Enabled</span></>
              ) : (
                <><ToggleLeft className="h-8 w-8 text-gray-400" /><span className="text-gray-400">Disabled</span></>
              )}
            </button>
          </div>

          {/* Brevo API Key */}
          <Field
            label="Brevo API Key"
            hint="Found in your Brevo account → SMTP & API → API Keys. Starts with xkeysib-..."
          >
            <div className="relative">
              <Input
                type={showKey ? 'text' : 'password'}
                placeholder={hasStoredKey ? 'Key saved — enter new key to replace' : 'xkeysib-...'}
                value={form.brevo_api_key}
                onChange={(e) => setForm((p) => ({ ...p, brevo_api_key: e.target.value }))}
                className="pr-10"
                data-testid="brevo-api-key"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </Field>

          {/* Sender email */}
          <Field
            label="Sender Email"
            hint="Must be verified in your Brevo account. E.g. bookings@vietclinics.com"
          >
            <Input
              type="email"
              placeholder="bookings@vietclinics.com"
              value={form.sender_email}
              onChange={(e) => setForm((p) => ({ ...p, sender_email: e.target.value }))}
              data-testid="sender-email"
            />
          </Field>

          {/* Sender name */}
          <Field label="Sender Name">
            <Input
              placeholder="VietClinics"
              value={form.sender_name}
              onChange={(e) => setForm((p) => ({ ...p, sender_name: e.target.value }))}
              data-testid="sender-name"
            />
          </Field>

          {/* Clinic notification email */}
          <Field
            label="Clinic Notification Email"
            hint="New booking alerts will also be sent to this address (e.g. admin@vietclinics.com)"
          >
            <Input
              type="email"
              placeholder="admin@vietclinics.com"
              value={form.clinic_notification_email}
              onChange={(e) => setForm((p) => ({ ...p, clinic_notification_email: e.target.value }))}
              data-testid="notification-email"
            />
          </Field>

          {/* Save button */}
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-[#1FA7A1] hover:bg-[#178f8a] text-white py-5 font-bold"
            data-testid="save-settings-btn"
          >
            {saving ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                Saving...
              </span>
            ) : (
              <span className="flex items-center gap-2"><Save className="h-4 w-4" /> Save Settings</span>
            )}
          </Button>
        </div>

        {/* Test section */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mt-6">
          <h2 className="font-bold text-gray-900 mb-1">Send Test Email</h2>
          <p className="text-sm text-gray-500 mb-5">Verify your configuration by sending a test booking confirmation.</p>
          <div className="flex gap-3">
            <Input
              type="email"
              placeholder="your@email.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1"
              data-testid="test-email-input"
            />
            <Button
              onClick={handleTest}
              disabled={testing}
              variant="outline"
              className="border-[#1FA7A1] text-[#1FA7A1] hover:bg-[#1FA7A1] hover:text-white gap-2"
              data-testid="send-test-btn"
            >
              {testing ? (
                <span className="w-4 h-4 border-2 border-current/50 border-t-current rounded-full animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              Test
            </Button>
          </div>
        </div>

        {/* How to get Brevo key */}
        <div className="mt-6 p-5 bg-blue-50 rounded-2xl border border-blue-100">
          <h3 className="font-semibold text-blue-900 text-sm mb-2">How to get your Brevo API Key</h3>
          <ol className="text-sm text-blue-800 space-y-1.5 list-decimal list-inside">
            <li>Go to <a href="https://my.sendinblue.com/" target="_blank" rel="noopener noreferrer" className="underline font-medium">my.sendinblue.com</a> and log in</li>
            <li>Click your account name → SMTP & API</li>
            <li>Go to the <strong>API Keys</strong> tab and click <strong>Generate a new API key</strong></li>
            <li>Copy the key (starts with <code className="bg-blue-100 px-1 rounded">xkeysib-</code>) and paste it above</li>
            <li>Ensure your sender email domain is verified under <strong>Senders & Domains</strong></li>
          </ol>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default EmailSettingsPage;
